import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fmtMoney, fmtDate, LEVEL_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

export default function Regulator() {
    const { id } = useParams();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.post(`/complaints/${id}/regulator-packet`).then((r) => setData(r.data)).catch(() => setData({ eligible: false }));
    }, [id]);

    if (!data) return <Layout><div className="speakup-card p-6">Building your packet…</div></Layout>;

    if (!data.eligible) {
        return (
            <Layout>
                <Link to={`/c/${id}`} className="text-sm text-[var(--speakup-text-muted)] hover:underline">← Back to complaint</Link>
                <div className="mt-3 speakup-card p-6 md:p-8" data-testid="regulator-not-eligible">
                    <h1 className="display text-[24px]">Not yet eligible for the RBI Ombudsman</h1>
                    <p className="mt-2 text-[var(--speakup-text-muted)]">{data.reason || "Need a reference number AND either a rejection or 30 days elapsed."}</p>
                    <ul className="mt-4 list-disc pl-5 text-[14px]">
                        <li>Reference number on file: {data.has_reference ? "✅" : "❌"}</li>
                        <li>Rejection recorded: {data.has_rejection ? "✅" : "❌"}</li>
                        <li>Days since first complaint: <span className="tabular">{data.days_elapsed ?? 0}</span> / 30</li>
                    </ul>
                </div>
            </Layout>
        );
    }

    const p = data.packet;
    const copyPacket = async () => {
        const lines = [
            `RBI Ombudsman Packet — ${p.entity_name}`,
            `Consumer: ${p.consumer_name} (${p.consumer_phone})`,
            `First complaint to ${p.entity_name}: ${fmtDate(p.first_complaint_date)} — ref ${p.reference_no}`,
            `Days elapsed: ${p.days_elapsed}`,
            `Rejection recorded: ${p.rejection_recorded ? "Yes" : "No"}`,
            ``,
            `Issue: ${p.issue_summary}`,
            p.amount_disputed ? `Amount disputed: ${fmtMoney(p.amount_disputed)}` : "",
            `Relief sought: ${p.relief_sought}`,
            ``,
            `Escalation history:`,
            ...(p.level_history || []).map(
                (h) => `· L${h.level} ${LEVEL_LABELS[h.level]} — sent ${fmtDate(h.sent_at)} · ref ${h.reference_no || "—"} · verdict ${h.verdict || "—"}`
            ),
        ].filter(Boolean);
        await navigator.clipboard.writeText(lines.join("\n"));
        toast.success("Packet copied");
    };

    return (
        <Layout>
            <Link to={`/c/${id}`} className="text-sm text-[var(--speakup-text-muted)] hover:underline">← Back to complaint</Link>
            <div className="mt-3 flex items-center gap-3">
                <span className="chip chip-regulator">RBI Ombudsman</span>
                <h1 className="display text-[26px] md:text-[30px]">Ready to file</h1>
            </div>
            <p className="text-[var(--speakup-text-muted)] mt-2">
                You qualify under the RB-IOS, 2021. You have two routes — the official CMS portal (recommended) or
                the CRPC email fallback. We've packaged everything below.
            </p>

            <div className="mt-6 grid md:grid-cols-2 gap-5">
                <div className="speakup-card p-5" data-testid="regulator-packet-card">
                    <div className="display text-[18px]">Your packet</div>
                    <dl className="mt-3 text-[14px] grid gap-2">
                        <Row k="Consumer" v={`${p.consumer_name} · ${p.consumer_phone}`} />
                        <Row k="Institution" v={p.entity_name} />
                        <Row k="First complaint" v={fmtDate(p.first_complaint_date)} />
                        <Row k="Reference number" v={<span className="tabular">{p.reference_no}</span>} />
                        <Row k="Days elapsed" v={<span className="tabular">{p.days_elapsed}</span>} />
                        <Row k="Rejection recorded" v={p.rejection_recorded ? "Yes" : "No"} />
                        <Row k="Amount disputed" v={fmtMoney(p.amount_disputed)} />
                        <Row k="Relief sought" v={p.relief_sought} />
                    </dl>
                    <button
                        onClick={copyPacket}
                        data-testid="regulator-copy-packet-btn"
                        className="mt-4 h-11 px-4 rounded-[10px] border border-[var(--speakup-border)] font-semibold"
                    >
                        Copy packet to clipboard
                    </button>
                </div>

                <div className="speakup-card p-5" data-testid="regulator-cms-card">
                    <div className="display text-[18px]">File via CMS portal (recommended)</div>
                    <p className="text-[14px] text-[var(--speakup-text-muted)] mt-1">
                        The CMS portal uses OTP and CAPTCHA — you file it, we guide you.
                    </p>
                    <ol className="mt-3 space-y-2 text-[14px] list-decimal pl-5">
                        {data.cms_steps.map((s) => (
                            <li key={s.slice(0, 80)}>{s}</li>
                        ))}
                    </ol>
                    <a
                        href={data.cms_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="regulator-cms-open-btn"
                        className="mt-4 inline-flex items-center gap-2 h-11 px-4 rounded-[10px] text-white font-semibold"
                        style={{ background: "var(--speakup-regulator)" }}
                    >
                        Open cms.rbi.org.in <ExternalLink size={14} />
                    </a>
                    <div className="text-[12px] text-[var(--speakup-text-muted)] mt-3 tabular">
                        Toll-free: 14448
                    </div>
                </div>
            </div>

            <div className="mt-6 speakup-card p-5" data-testid="regulator-crpc-card">
                <div className="display text-[18px]">Or — CRPC email fallback</div>
                <p className="text-[14px] text-[var(--speakup-text-muted)] mt-1">
                    Some users can't use the portal. The Consumer Relations & Protection Cell accepts complaints by email.
                </p>
                <div className="mt-3 text-[14px] grid gap-1">
                    <div><span className="text-[var(--speakup-text-muted)] uppercase text-[11px] tracking-wider mr-2">TO</span>{data.crpc_email}</div>
                    <div><span className="text-[var(--speakup-text-muted)] uppercase text-[11px] tracking-wider mr-2">SUBJECT</span>{data.crpc_subject}</div>
                </div>
                <pre className="mt-3 p-4 bg-[var(--speakup-surface-muted)] rounded-[10px] text-[13px] whitespace-pre-wrap leading-relaxed" data-testid="regulator-crpc-body-preview">
                    {data.crpc_body}
                </pre>
                <div className="mt-4 flex flex-wrap gap-3">
                    <a href={data.gmail_url} target="_blank" rel="noreferrer" data-testid="regulator-crpc-gmail-btn"
                       className="h-12 inline-flex items-center justify-center px-5 rounded-[12px] text-white font-semibold"
                       style={{ background: "var(--speakup-red)" }}>Open Gmail</a>
                    <a href={data.mailto_url} data-testid="regulator-crpc-mailto-btn"
                       className="h-12 inline-flex items-center justify-center px-5 rounded-[12px] border-2 font-semibold"
                       style={{ borderColor: "var(--speakup-red)", color: "var(--speakup-red)" }}>Open my email</a>
                </div>
            </div>
        </Layout>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex items-baseline gap-3">
            <div className="w-36 text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)]">{k}</div>
            <div className="flex-1 text-[var(--speakup-text-strong)]">{v ?? "—"}</div>
        </div>
    );
}
